import { downloadFile } from '@shared/helpers/download-file.helper';
import { Config } from '@shared/configs/config';
import { AuthFilePipe } from '@shared/pipes/authFile.pipe';
import { TranslateService } from '@ngx-translate/core';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import {
  Component,
  OnInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Input,
  ViewChild,
  ElementRef,
  EventEmitter,
  Output,
  OnDestroy,
  signal,
  input,
  inject
} from '@angular/core';
import { Attachment } from '@shared/interfaces/attachment.interface';
import { ButtonTypes, ButtonSize } from '@shared/modules/ui/components/button/button.component';
import { BaseAttachmentComponent } from '@shared/modules/attachments/components/base-attachment/base-attachment.component';
import { SnackBarService } from '@core/services/snackbar.service';
import { AttachmentsManageService } from '../../services/attachments-manage.service';
import { TextAttachment } from '@shared/components/text-attachments-input/models/text-attachment.model';
import { EMPTY, interval, Subscription } from 'rxjs';
import * as moment from 'moment';
import { RemoveModalController } from '@shared/controllers/remove-modal.controller';
import { TextAttachmentsConfig } from '@shared/components/text-attachments-input/models/text-attachments-config';
import { IntilioCodes } from '@shared/enums/initilio-codes.enum';
import { ListTaskService } from '@shared/modules/list/services/list-task.service';
import { catchError, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import {
  AttachmentLibraryDialogResult,
  AttachmentLibraryModalComponent
} from '../attachment-library-modal/attachment-library-modal.component';

@Component({
  selector: 'attachment-manager-list-v2',
  templateUrl: './attachment-manager-list-v2.component.html',
  styleUrls: ['./attachment-manager-list-v2.component.scss'],
  providers: [AttachmentsManageService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AttachmentManagerListV2Component extends BaseAttachmentComponent implements OnInit, OnDestroy {
  readonly projectId = input<number | null>(null);
  ButtonTypes = ButtonTypes;
  ButtonSize = ButtonSize;
  counter: number = 0;
  entered: boolean = false;

  date: number = moment().unix();

  form: UntypedFormGroup;
  changingState: boolean = false;

  _attachments: Attachment[] = [];
  _showLimit: boolean = true;
  showAllFiles: boolean = false;
  fileLoading: boolean = false;
  fileDeleting: boolean = false;
  fileZipLoading: boolean = false;
  selectedFilesCount: number = 0;
  groupName: string = 'attachments';
  filesUploadCounter: number = 0;
  attachmentSub: Subscription;

  protected fileUploadProgress = signal<Map<string | number, number>>(new Map());
  private readonly dialog = inject(MatDialog);

  @Output('singleFileUploaded') singleFileUploaded: EventEmitter<any> = new EventEmitter();
  @Output('filesUploaded') filesUploaded: EventEmitter<boolean> = new EventEmitter();
  @Output('onAttachmentsChange') onAttachmentsChange: EventEmitter<Attachment[]> = new EventEmitter<
    Attachment[]
  >();

  @ViewChild('fileInput') fileInput: ElementRef;

  @Input() disabled: boolean;
  @Input() isPreview: boolean;
  @Input() attachmentsConfig: TextAttachmentsConfig;
  @Input() titleTranslationKey: string = 'attachments';

  @Input() apiConfig: any = null;
  @Input() uploadConfig: { id: number; objectType: string } = null;

  @Input() limit: number = 8;
  @Input()
  set showLimit(_showLimit: boolean) {
    this._showLimit = _showLimit;
    this.showAllFiles = !_showLimit;
  }
  get showLimit() {
    return this._showLimit;
  }

  @Input()
  set fileAttachments(_attachments: any[]) {
    const incomingAttachments = (_attachments || []).map((i) => new Attachment(i));
    const uploadingAttachments = this._attachments.filter((i) => !i?.isUploaded);
    const mergedAttachments = [...incomingAttachments];

    uploadingAttachments.forEach((attachment) => {
      const exists = mergedAttachments.some((item) => item?.id?.toString() === attachment?.id?.toString());
      if (!exists) {
        mergedAttachments.unshift(attachment);
      }
    });

    mergedAttachments.forEach((i: Attachment) => this.createFormControl(i));
    this._attachments = mergedAttachments;
    this.sortAttachmentsByDate();
    this.changes.detectChanges();
  }
  get fileAttachments() {
    return this._attachments;
  }

  get isDoubleAttachments() {
    return this.attachmentsConfig?.photoAttachments && this.attachmentsConfig?.fileAttachments;
  }

  get selectedFiles() {
    const ids = [];
    Object.keys((this.form.get(this.groupName) as UntypedFormGroup).controls).forEach((key: string) => {
      if (!!this.form.get(this.groupName).get(key).value) {
        ids.push(parseInt(key));
      }
    });
    return ids;
  }

  constructor(
    private fb: UntypedFormBuilder,
    public translate: TranslateService,
    public changes: ChangeDetectorRef,
    public s: SnackBarService,
    public authFilePipe: AuthFilePipe,
    public service: AttachmentsManageService,
    private listTaskService: ListTaskService
  ) {
    super(translate, changes, s, authFilePipe);
    this.createForm();
  }

  ngOnInit() {
    this.subscribeOptionAllChange();
    this.subscribeOptionChange();
    this.subscribeFileUpload();
  }

  createForm() {
    this.form = this.fb.group({ [this.groupName]: this.fb.group({}), all: false });
  }

  dragEnter(e) {
    if (this.disabled) return;
    e.preventDefault(); // needed for IE
    this.counter++;
    this.entered = true;
    this.changes.detectChanges();
  }

  dragLeave() {
    if (this.disabled) return;
    this.counter--;
    if (this.counter === 0) {
      this.entered = false;
    }
    this.changes.detectChanges();
  }

  dragDropped() {
    if (this.disabled) return;
    this.counter = 0;
    this.entered = false;
    this.changes.detectChanges();
  }

  subscribeOptionAllChange() {
    this.form.get('all').valueChanges.subscribe((checked: boolean) => {
      if (this.changingState) {
        return;
      }
      const formGroup = this.form.get(this.groupName) as UntypedFormGroup;
      const controls = formGroup.controls;

      Object.keys(controls).forEach((key: string) => formGroup.get(key).setValue(checked));
      this.changes.detectChanges();
    });
  }

  subscribeOptionChange() {
    this.form.get(this.groupName).valueChanges.subscribe((values) => {
      this.changingState = true;
      const newValues = [...Object.values(values)];
      this.selectedFilesCount = Object.values(values).filter((i) => !!i).length;

      if (!!newValues.length) {
        if (newValues.indexOf(false) > -1) {
          !!this.form.get('all').value ? this.form.get('all').setValue(false) : null;
        } else {
          !this.form.get('all').value ? this.form.get('all').setValue(true) : null;
        }
      }
      this.changingState = false;
      this.changes.detectChanges();
    });
  }

  subscribeFileUpload() {
    this.uploadFile.subscribe((obj) => {
      //1. Upload file to server
      this.uploadFileToServer(obj.file, obj.callback);
      //2. Show mocked attachment
      this.fileAttachments = [obj.file].concat([...this.fileAttachments]);
      this.changes.detectChanges();
    });

    this.attachmentSub = this.listTaskService.attachmentSub$
      .pipe(tap((data) => this.onFilesChange([data])))
      .subscribe();
  }

  uploadFileToServer(file: TextAttachment, callback?: Function) {
    this.uploadConfig
      ? this.uploadSingleAttachment(file, callback)
      : this.uploadFilesViaPacket(file, callback);
  }

  uploadSingleAttachment(file: TextAttachment, callback?: Function) {
    this.service.attachmentService
      .uploadAttachmentWithProgress(
        {
          objectId: this.uploadConfig.id,
          objectType: this.uploadConfig.objectType,
          files: [file.file]
        },
        this.apiConfig
      )
      .pipe(
        tap(({ result, progress }) => {
          console.log('progress', progress);
          this.updateProgress(file.id, progress);
          if (result) this.successAttachments(file, result, callback);
        }),
        catchError((error) => {
          this.updateProgress(file.id, 0);
          this.handleUploadError(error, file);
          return EMPTY;
        })
      )
      .subscribe();
  }

  uploadFilesViaPacket(file: TextAttachment, callback?: Function) {
    !this.fileAttachments.length
      ? this.createAttachmentPacket(file, callback)
      : this.updateAttachmentPacket(file, callback);
  }

  createAttachmentPacket(file: TextAttachment, callback?: Function) {
    this.service.createAttachmentPacket([file.file], this.apiConfig).subscribe({
      next: ({ result, progress }) => {
        console.log('progress', progress);
        this.updateProgress(file.id, progress);
        if (result) this.successAttachments(file, result.attachments, callback);
      },
      error: (error) => this.handleUploadError(error, file)
    });
  }

  createAttachments(list) {
    list.forEach((attachment) => {
      const findIndex = this._attachments.findIndex((i) => i.id === attachment.id);
      if (findIndex === -1) {
        attachment = new Attachment(attachment);
        attachment.isUploaded = true;
        this._attachments.unshift(attachment);
        this.createFormControl(attachment);
        this.sortAttachmentsByDate();
        this.changes.detectChanges();
      }
    });
  }

  updateAttachmentPacket(file: TextAttachment, callback?: Function) {
    const source$ = interval(400)
      .pipe()
      .subscribe(() => {
        if (!!this.service?.attachmentPacket?.id) {
          this.updateAttachmentPacketUploaded(file);
          source$.unsubscribe();
        }
      });
  }

  sortAttachmentsByDate() {
    if (!this._attachments.length) return;
    this._attachments.sort(
      (a, b) => moment(b.created, Config.DATE_SERVER).unix() - moment(a.created, Config.DATE_SERVER).unix()
    );
  }

  sortAttachmentsByName() {
    if (!this._attachments.length) return;
    this._attachments.sort((a, b) => a.name.localeCompare(b.name));
  }

  updateAttachmentPacketUploaded(file: TextAttachment, callback?: Function) {
    this.service
      .updateAttachmentPacket(this.service?.attachmentPacket?.id, [file.file], this.apiConfig)
      .subscribe({
        next: ({ result, progress }) => {
          console.log('progress', progress);
          this.updateProgress(file.id, progress);
          if (result) this.successAttachments(file, result.attachments, callback);
        },
        error: (error) => this.handleUploadError(error, file)
      });
  }

  successAttachments(file, list, callback?: Function) {
    callback ? callback(list.length ? list[list.length - 1] : null) : null;
    this.removeUploadingAttachment(file);
    this.createAttachments(list);
    this.onAttachmentsChange.emit(this.fileAttachments);
  }

  successLoadFilesEnd() {
    this.onAttachmentsChange.emit(this.fileAttachments);
  }

  removeUploadingAttachment(file: TextAttachment) {
    this.removeUploadingAttachmentById(parseInt(file?.id?.toString()));
  }

  removeUploadingAttachmentById(id: number, updateService: boolean = false) {
    const index = this.fileAttachments.findIndex((i) => i.id.toString() === id.toString());
    if (index === -1) return;

    this.fileAttachments.splice(index, 1);

    if (updateService) {
      const serviceIndex = this.service.attachmentPacket.attachments.findIndex(
        (i) => i.id.toString() === id.toString()
      );
      if (serviceIndex > -1) {
        this.service.attachmentPacket.attachments.splice(serviceIndex, 1);
      }
    }

    this.changes.detectChanges();
    (this.form.get(this.groupName) as UntypedFormGroup).removeControl(id?.toString());
    this.changes.detectChanges();
  }

  createFormControl(atttachment: Attachment) {
    const formGroup = this.form.get(this.groupName) as UntypedFormGroup;
    if (formGroup.get(atttachment?.id?.toString())) return;

    formGroup.addControl(atttachment?.id.toString(), new UntypedFormControl(false));
  }

  trackAttachments(index: number, attachment: Attachment) {
    return attachment.id;
  }

  downloadSelectedFiles() {
    if (this.fileZipLoading) return;
    this.fileZipLoading = true;
    this.changes.detectChanges();

    const data: string[] = this.fileAttachments
      .filter((i) => {
        return !!this.selectedFiles.filter((j) => j === i.id)[0];
      })
      .map((i) => i.file as any);

    this.service.attachmentService
      .getAttachmentZip(data)
      .subscribe({
        next: (zip) => this.successGettingZip(zip)
      })
      .add(() => {
        this.fileZipLoading = false;
        this.changes.detectChanges();
      });
  }

  openAttachmentLibrary(): void {
    const projectId = this.projectId();
    if (this.disabled || this.fileLoading) return;

    const maxAttachments = this.attachmentsConfig?.maxAttachments;
    const maxSelectable = maxAttachments ? Math.max(maxAttachments - this.fileAttachments.length, 0) : null;
    const dialogRef = this.dialog.open(AttachmentLibraryModalComponent, {
      width: '1120px',
      height: 'calc(100vh - 96px)',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: '760px',
      autoFocus: false,
      disableClose: true,
      data: {
        projectId,
        attachmentsConfig: this.attachmentsConfig,
        attachedIds: this.fileAttachments.map((attachment) => attachment.id),
        maxSelectable
      }
    });

    dialogRef.afterClosed().subscribe((result?: AttachmentLibraryDialogResult) => {
      if (!result?.sourceAttachmentIds?.length) return;
      this.attachProjectLibraryFiles(projectId, result.sourceAttachmentIds);
    });
  }

  successGettingZip(zip) {
    downloadFile(zip, '[intilio] file-attachments-' + moment().unix());
  }

  deleteSelectedFiles() {
    const ctrl = new RemoveModalController();
    ctrl
      .remove(() => {
        this.deleteAttachmentGroup();
      })
      .subscribe(() => {});
  }

  deleteAttachmentGroup() {
    if (this.fileDeleting || !this.selectedFiles?.length) return;
    this.fileDeleting = true;
    this.changes.detectChanges();
    const ids = this.selectedFiles;
    this.service.attachmentService
      .removeAttachmentGroup(ids)
      .subscribe({
        next: () => this.successDeletingAttachments(ids)
      })
      .add(() => {
        this.fileDeleting = false;
        this.changes.detectChanges();
      });
  }

  successDeletingAttachments(ids: number[]) {
    ids.forEach((id: number) => this.removeUploadingAttachmentById(id, true));
    this.onAttachmentsChange.emit(this.fileAttachments);
  }

  addSourceAttachments(projectId: number, sourceAttachmentIds: number[]): void {
    if (!projectId || this.fileLoading) return;

    const ids = [...new Set((sourceAttachmentIds || []).map(Number).filter((id) => Number.isFinite(id) && id > 0))]
      .slice(0, 200);
    if (!ids.length) return;

    this.attachProjectLibraryFiles(projectId, ids);
  }

  private attachProjectLibraryFiles(projectId: number | null, sourceAttachmentIds: number[]): void {
    const ids = sourceAttachmentIds.slice(0, 200);
    this.fileLoading = true;
    this.changes.detectChanges();

    const source$ = this.uploadConfig
      ? this.service.attachmentService.attachProjectLibraryFiles(
          this.uploadConfig.id,
          this.uploadConfig.objectType,
          projectId,
          ids
        )
      : this.service.attachmentPacket?.id
        ? this.service.updateAttachmentPacketFromSources(this.service.attachmentPacket.id, projectId, ids)
        : this.service.createAttachmentPacketFromSources(projectId, ids);

    source$.subscribe({
      next: (response: any) => {
        const attachments = this.extractAttachments(response);
        this.createAttachments(attachments);
        this.onAttachmentsChange.emit(this.fileAttachments);
      },
      error: () => this.s.error(this.translate.instant('Attachments.library.errorAdding'))
    }).add(() => {
      this.fileLoading = false;
      this.changes.detectChanges();
    });
  }

  private extractAttachments(response: any): Attachment[] {
    const value = Array.isArray(response)
      ? response
      : response?.attachments || response?.result?.attachments || (response?.id ? [response] : []);
    return (value || []).map((attachment) => new Attachment(attachment));
  }

  private handleUploadError(error: any, file: TextAttachment) {
    this.removeUploadingAttachment(file);
    if (error.messageCode === IntilioCodes.FILE_IS_BIG) {
      this.s.error(this.translate.instant('Settings.User.Errors.errorFileTooBig').replace('{{size}}', 10));
    } else {
      this.s.error(this.translate.instant('Attachments.errorUploading'));
    }
  }

  ngOnDestroy(): void {
    this.attachmentSub?.unsubscribe();
  }

  private updateProgress(fileId: string | number, progress: number) {
    this.fileUploadProgress.update((map) => {
      const newMap = new Map(map);
      const currentProgress = newMap.get(fileId) ?? 0;

      if (progress === 0 && currentProgress > 0 && currentProgress < 100) return map;

      if (progress >= 100) {
        newMap.set(fileId, 100);
        setTimeout(() => this.clearProgress(fileId), 1000);
      } else {
        newMap.set(fileId, progress);
      }

      return newMap;
    });
  }

  private clearProgress(fileId: string | number) {
    this.fileUploadProgress.update((map) => {
      const newMap = new Map(map);
      newMap.delete(fileId);
      return newMap;
    });
  }
}
